import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAppStore } from '@/store';
import { Button, PanelBoundary } from '../shared';
import { SidebarNav } from 'calc-sidebar';
import type { SidebarItemDef } from 'calc-sidebar';
import { OverviewPanel } from './OverviewPanel';
import { CalendarGrid } from '../CalendarView/CalendarGrid';
import { LogAbsencePanel } from './LogAbsencePanel';
import { GradeImpactPanel } from './GradeImpactPanel';
import { WhatIfPanel } from '../WhatIfPanel/WhatIfPanel';
import { PrintReport } from '../Reports/PrintReport';
import { TermSummary } from '../Reports/TermSummary';
import { ParticipationLogPanel } from '../ParticipationLog/ParticipationLogPanel';
import { TrendAnalytics } from '../TrendAnalytics/TrendAnalyticsPanel';

export function CourseDetailLayout(): React.ReactElement | null {
  const selectedCourseId = useAppStore((s) => s.selectedCourseId);
  const selectCourse = useAppStore((s) => s.selectCourse);
  const courses = useAppStore((s) => s.courses);

  const course = courses.find((c) => c.id === selectedCourseId);
  if (!course) return null;

  // Course detail has 8 top-level groups, exceeding the 5-tab horizontal cap,
  // so we use calc-sidebar's SidebarNav. All panels stay mounted (hidden via
  // the `hidden` attribute) so per-panel state survives navigation.
  const items: SidebarItemDef[] = [
    { id: 'overview', label: 'Overview', content: <OverviewPanel courseId={course.id} /> },
    { id: 'calendar', label: 'Calendar', content: <CalendarGrid courseId={course.id} /> },
    { id: 'log', label: 'Log Absence', content: <LogAbsencePanel courseId={course.id} /> },
    { id: 'grade', label: 'Grade Impact', content: <GradeImpactPanel courseId={course.id} /> },
    { id: 'whatif', label: 'What-If', content: <WhatIfPanel courseId={course.id} /> },
    { id: 'participation', label: 'Participation', content: <ParticipationLogPanel courseId={course.id} /> },
    { id: 'analytics', label: 'Analytics', content: <TrendAnalytics course={course} /> },
    {
      id: 'reports',
      label: 'Reports',
      content: (
        <PanelBoundary panelName="Reports">
          <PrintReport course={course} />
          <TermSummary course={course} />
        </PanelBoundary>
      ),
    },
  ];

  return (
    <main className="course-detail" id="main-content">
      <div className="course-detail__header">
        <Button variant="ghost" size="sm" onClick={() => selectCourse(null)} aria-label="Back to dashboard">
          <ArrowLeft size={16} aria-hidden="true" />
        </Button>
        <div className="course-detail__info">
          <h2 className="course-detail__name">{course.name}</h2>
          <span className="course-detail__code">{course.code} &middot; {course.instructor}</span>
        </div>
      </div>

      <SidebarNav section="course-detail" stickyTop="0px" items={items} />
    </main>
  );
}
